import { UseQueryResult } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Contact } from '../../../shared/types';
import GpanelContactsGridToolbar from './gpanel-contacts-grid-toolbar';
import SharedGpanelContactsGrid from '../../../shared/components/shared-gpanel-contacts-grid';
import { ReactNode, useState } from 'react';
import { createGridOverlays } from '../../../../../grid-overlay/no-data-overlay-helpers';
import MenuItem from '@mui/material/MenuItem';
// import GroupOutlined from '@mui/icons-material/GroupOutlined';
import TooltipWithPermission from '../../../shared/components/tooltip-with-permission.tsx';
import { useGpanelContactsPermissions } from '../../../shared/hooks/use-gpanel-contacts-permissions.tsx';
import { GPanelContactsPermissions } from '../../../../../../utils/roles/client-roles.ts';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import GPanelContactsModal from '../gpanel-contacts-form-page.tsx';
import GPanelContactsImportDialog from '../gpanel-contacts-import-dialog.tsx';

import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Contacts from '@mui/icons-material/Contacts';
import ImportContacts from '@mui/icons-material/ImportContacts';
import Person from '@mui/icons-material/Person';

const columns: GridColDef<Contact>[] = [
  {
    field: 'contactName',
    headerName: 'Contact Name',
    flex: 1,
    valueGetter: (_value, row) =>
      row.fileAs?.trim()
        ? row.fileAs
        : `${row.firstName ?? ''} ${row.lastName ?? ''}`,
    renderCell: (param) => {
      const displayName = param.row.fileAs?.trim()
        ? param.row.fileAs
        : `${param.row.firstName ?? ''} ${param.row.lastName ?? ''}`;
      return (
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography variant="body2">{displayName}</Typography>
        </Stack>
      );
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.emails?.[0]?.value ?? '';
    },
  },
  {
    field: 'phoneNumbers',
    headerName: 'Phone Number',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.phoneNumbers?.[0]?.value ?? '';
    },
  },
  {
    field: 'jobTitle',
    headerName: 'Job Title',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.companyJobTitle ?? '';
    },
  },
  {
    field: 'companyName',
    headerName: 'Company Name',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.companyName ?? '';
    },
  },
  {
    field: 'companyDepartment',
    headerName: 'Department',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.companyDepartment ?? '';
    },
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.createdBy ?? '';
    },
  },
  {
    field: 'createdTime',
    headerName: 'Created Time',
    flex: 1,
    valueGetter: (_value, row) =>
      row.createdTime ? new Date(row.createdTime).toLocaleString() : '',
  },
  {
    field: 'updatedBy',
    headerName: 'Updated By',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.updatedBy ?? '';
    },
  },
  {
    field: 'updatedTime',
    headerName: 'Updated Time',
    flex: 1,
    valueGetter: (_value, row) =>
      row.updatedTime ? new Date(row.updatedTime).toLocaleString() : '',
  },
  {
    field: 'notes',
    headerName: 'Notes',
    flex: 1,
    valueGetter: (_value, row) => row.notes ?? '',
  },
  {
    field: 'websites',
    headerName: 'Websites',
    flex: 1,
    valueGetter: (_value, row) => {
      return row?.websites?.[0]?.value ?? '';
    },
  },
  {
    field: 'labels',
    headerName: 'Labels',
    flex: 1,
    sortable: false,
    valueGetter: (_value, row) => row.activeLabels?.join(', ') ?? '',
    renderCell: ({ row }) =>
      row.activeLabels?.length ? (
        <Stack
          flexDirection="row"
          gap={1}
          sx={{ overflowX: 'auto', overflowY: 'hidden' }}
        >
          {row.activeLabels.map((label: string) => {
            const isLong = label.length > 40;
            const displayLabel = isLong ? `${label.slice(0, 40)}...` : label;

            const chip = <Chip key={label} label={displayLabel} size="small" />;

            return isLong ? (
              <Tooltip key={label} title={label}>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Stack>
      ) : null,
  },
];

export default function GpanelContactsGrid(props: {
  query: UseQueryResult<Contact[], Error>;
  onRefresh: () => void;
  title: string;
  syncRunning?: boolean;
  contactsToolbarActions?: (selection: number[]) => ReactNode;
  withHeaderActions?: boolean;
  labelId?: number;
  gridKey?: string | null;
}) {
  const { PERMISSIONS } = useGpanelContactsPermissions();

  const [selection, setSelection] = useState<number[]>([]);

  const defaultGridState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    columns: {
      columnVisibilityModel: {
        companyName: false,
        companyDepartment: false,
        updatedTime: false,
        updatedBy: false,
        createdTime: false,
        websites: false,
        notes: false,
      },
    },
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openCreateContact, setOpenCreateContact] = useState(false);
  const [openImportContact, setOpenImportContact] = useState(false);

  const headerActions = props.withHeaderActions ? (
    <Stack flexDirection="row" alignItems="right" gap={1}>
      {/* Create/Import Contact Button and Menu */}
      <TooltipWithPermission
        title="Create Contact"
        hasPermission={PERMISSIONS.createContact}
        permissionName={GPanelContactsPermissions.CreateContact.name}
        renderElement={(hasPermission) => (
          <Button
            startIcon={<Add />}
            endIcon={<ArrowDropDown />}
            variant="contained"
            disabled={!hasPermission}
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            sx={{ textTransform: 'none', height: '40px' }}
          >
            Create Contact
          </Button>
        )}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ '& .MuiPaper-root': { minWidth: 190 } }}
      >
        <MenuItem
          onClick={() => {
            setOpenCreateContact(true);
            setMenuAnchorEl(null);
          }}
        >
          <Person fontSize="small" sx={{ mr: 1, opacity: 0.54 }} /> Create
          Contact
        </MenuItem>
        <TooltipWithPermission
          hasPermission={PERMISSIONS.importContacts}
          permissionName={GPanelContactsPermissions.ImportContacts.name}
          renderElement={(hasPermission) => (
            <MenuItem
              onClick={() => {
                setOpenImportContact(true);
                setMenuAnchorEl(null);
              }}
              disabled={!hasPermission}
            >
              <ImportContacts fontSize="small" sx={{ mr: 1, opacity: 0.54 }} />{' '}
              Import Contacts
            </MenuItem>
          )}
        />
      </Menu>
    </Stack>
  ) : null;

  return (
    <>
      <SharedGpanelContactsGrid
        {...props}
        columns={columns}
        initialGridState={defaultGridState}
        toolbar={GpanelContactsGridToolbar as never}
        selection={selection}
        onSelection={setSelection}
        title={props.title}
        {...createGridOverlays(
          'Contacts',
          <Contacts />,
          `There may be no Contacts in the ${props.title}.`,
          'No Contacts Found'
        )}
        headerActions={headerActions}
        gridStateKey="gpanelContactsGridStates"
      />
      {/* Create/Import Contact Dialogs */}
      {openCreateContact && (
        <GPanelContactsModal
          open={true}
          handleClose={() => setOpenCreateContact(false)}
          labelId={props.labelId}
        />
      )}
      <GPanelContactsImportDialog
        open={openImportContact}
        handleClose={() => setOpenImportContact(false)}
      />
    </>
  );
}
